import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Save, RefreshCw, Search, CheckCircle, AlertCircle, 
  Loader2, ArrowRight, Filter
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Catégories système de l'utilisateur
const SYSTEM_CATEGORIES = [
  "Véhicules - Achat",
  "Véhicules - Crédit Bail",
  "Équipements - Crédit Bail",
  "Assurances",
  "Loyer",
  "Assurances Vie",
  "Téléphonie",
  "Abonnements Logiciels",
  "Essence",
  "Publicité - Google Ads",
  "Publicité - Meta",
  "Publicité - Autres",
  "Recrutement - Indeed",
  "Matériaux",
  "Peinture - Sherwin-Williams",
  "Peinture - Betonel",
  "Peinture - Juneau & Frères",
  "Peinture - Matériaux Audet",
  "Peinture Benjamin Moore",
  "Comptabilité",
  "Frais bancaires",
  "Salaires Accès D",
  "Sous-traitants - LMDS",
  "Sous-traitants - PRQC",
  "Conseil et support",
  "Licenses et conformité",
  "Site internet - Wordpress",
  "Services Administratifs",
  "Vêtements et équipements",
  "Location d'outils",
  "Stationnement",
  "Amendes et pénalités",
  "Bien-être",
  "Boissons et nourriture",
  "Entretien véhicules",
  "Publicité imprimée",
  "Activités corporatives",
  "Entretien et réparations",
  "Taxes sur achats majeurs",
  "Fournitures de bureau",
  "Outillage",
  "Matériel Roulant",
  "Immobilier et aménagements",
  "Technologie et numérique",
  "Formation et capital humain",
  "Investissements en croissance",
  "Sécurité et conformité",
  "Investissements financiers",
  "Dividendes aux actionnaires",
  "Impôts des sociétés prévisionnels",
  "Taxes prévisionnelles",
  "Frais de déplacement",
  "Frais d'hébergement"
];

export const MappingCategoriesQB = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [qbCategories, setQbCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMapped, setFilterMapped] = useState("all");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les catégories QB depuis les règles
      const rulesRes = await axios.get(`${api.baseUrl}/quickbooks/classification-rules`);
      const rulesData = rulesRes.data;
      
      // Extraire catégories uniques
      const uniqueCategories = new Set();
      (rulesData.rules || []).forEach(rule => {
        const cat = rule.qb_category;
        // Exclure SPLIT et les comptes de transfert
        if (cat && cat !== "SPLIT" && !cat.startsWith('1010') && !cat.startsWith('1020') && !cat.startsWith('1030') && !cat.startsWith('1300') && !cat.startsWith('2019') && !cat.startsWith('2100') && !cat.startsWith('2130')) {
          uniqueCategories.add(cat);
        }
      });
      
      setQbCategories(Array.from(uniqueCategories).sort());

      // Charger les mappings existants
      const mappingsRes = await axios.get(`${api.baseUrl}/quickbooks/category-mappings`);
      const mappingsData = mappingsRes.data;
      
      setMappings(mappingsData.mappings || []);
      
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const saveMapping = async (qbCategory, systemCategory) => {
    setSaving(true);
    try {
      const response = await axios.post(`${api.baseUrl}/quickbooks/category-mapping`, {
          qb_category: qbCategory,
          system_category: systemCategory === "none" ? null : systemCategory
        });

      if (response.status === 200) {
        toast.success(`✅ Mapping enregistré`);
        loadData();
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      console.error("Erreur save:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const createSuggestedMappings = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${api.baseUrl}/quickbooks/create-suggested-mappings`, null);

      if (response.status === 200) {
        toast.success("✅ Mappings suggérés créés");
        loadData();
      } else {
        toast.error("Erreur lors de la création des suggestions");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  // Créer un dictionnaire des mappings existants
  const mappingsDict = {};
  mappings.forEach(m => {
    mappingsDict[m.qb_category] = m.system_category;
  });

  // Filtrer les catégories
  const filteredCategories = qbCategories.filter(cat => {
    const matchesSearch = cat.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterMapped === "mapped") {
      return matchesSearch && mappingsDict[cat];
    } else if (filterMapped === "unmapped") {
      return matchesSearch && !mappingsDict[cat];
    }
    
    return matchesSearch;
  });

  const stats = {
    total: qbCategories.length,
    mapped: Object.keys(mappingsDict).filter(k => mappingsDict[k]).length,
    unmapped: qbCategories.length - Object.keys(mappingsDict).filter(k => mappingsDict[k]).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-3xl font-bold text-slate-900">
          Mapping Catégories QuickBooks → Système
        </h1>
        <p className="text-slate-500 mt-1">
          Traduisez vos catégories comptables QB vers vos catégories analytiques
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 animate-fade-in delay-100">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Catégories QB</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <Filter className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Mappées</p>
                <p className="text-2xl font-bold text-green-600">{stats.mapped}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Non mappées</p>
                <p className="text-2xl font-bold text-amber-600">{stats.unmapped}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Button 
              onClick={createSuggestedMappings}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Suggestions Auto
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">💡 Comment ça fonctionne:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Les 96 règles QB classifient vos transactions vers des catégories QuickBooks</li>
                <li>Ici, vous traduisez ces catégories QB vers VOS catégories analytiques</li>
                <li>La ventilation par département sera appliquée automatiquement après</li>
                <li>Catégories avec "None" = Revenus ou transferts internes (ignorés des dépenses)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtres */}
      <Card className="animate-fade-in delay-200">
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher une catégorie QB..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterMapped} onValueChange={setFilterMapped}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="mapped">Mappées</SelectItem>
                <SelectItem value="unmapped">Non mappées</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des mappings */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle>Catégories QuickBooks ({filteredCategories.length})</CardTitle>
          <CardDescription>
            Associez chaque catégorie QB à votre catégorie système. Utilisez "Ignorer" pour les revenus et transferts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredCategories.map((qbCat) => (
              <MappingRow
                key={qbCat}
                qbCategory={qbCat}
                currentMapping={mappingsDict[qbCat]}
                systemCategories={SYSTEM_CATEGORIES}
                onSave={saveMapping}
                saving={saving}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const MappingRow = ({ qbCategory, currentMapping, systemCategories, onSave, saving }) => {
  const [selectedCategory, setSelectedCategory] = useState(currentMapping || "none");
  const [hasChanges, setHasChanges] = useState(false);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(qbCategory, selectedCategory);
    setHasChanges(false);
  };

  const isMapped = currentMapping && currentMapping !== "none";

  return (
    <div className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="font-mono text-sm font-medium text-slate-900">{qbCategory}</p>
          {isMapped && (
            <Badge variant="success" className="bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Mappée
            </Badge>
          )}
        </div>
      </div>

      <ArrowRight className="w-5 h-5 text-slate-400" />

      <div className="flex items-center gap-2">
        <Select value={selectedCategory} onValueChange={handleCategoryChange}>
          <SelectTrigger className="w-80">
            <SelectValue placeholder="Choisir une catégorie système" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-slate-500 italic">Ignorer (revenu/transfert)</span>
            </SelectItem>
            {systemCategories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasChanges && (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default MappingCategoriesQB;
